import type {
  CenaTabletop,
  NovoObjetoTabletop,
  ObjetoTabletop,
  TipoObjetoTabletop,
} from './tipos'

/**
 * Cenas prontas para a aula. Ids fixos: o roteiro e o debriefing citam os
 * objetos pelo id, e duas cargas do mesmo preset precisam ser iguais.
 */

const objeto = (
  id: string,
  tipo: TipoObjetoTabletop,
  nome: string,
  x: number,
  z: number,
  rotacaoYGraus = 0,
  extra: Partial<NovoObjetoTabletop> = {},
): ObjetoTabletop => ({
  id,
  tipo,
  nome,
  transformacao: extra.transformacao ?? {
    posicao: [x, 0, z],
    rotacao: [0, (rotacaoYGraus * Math.PI) / 180, 0],
    escala: [1, 1, 1],
  },
  ...(extra.fonte ? { fonte: extra.fonte } : {}),
})

export interface PresetMesa {
  id: string
  titulo: string
  resumo: string
  cena: CenaTabletop
}

export const PRESETS_MESA: readonly PresetMesa[] = [
  {
    id: 'glp-br',
    titulo: 'Tombamento de carreta de GLP na rodovia',
    resumo:
      'Carreta de GLP tombada no acostamento, escola e posto próximos. Começa estável; o roteiro leva a vazamento e fogo.',
    cena: {
      versao: 1,
      nome: 'Tombamento de carreta de GLP',
      historiaInicial:
        'Carreta-tanque tombada no acostamento da rodovia. Motorista saiu da cabine e está desorientado próximo ao veículo. Há uma escola a 300 m e um posto de combustível a 120 m.',
      meteorologia: {
        direcaoVentoGraus: 90,
        ventoMS: 3,
        periodo: 'dia',
        ceuEncoberto: false,
        terreno: 'aberto',
      },
      acionamento: {
        natureza: 'Acidente com produto perigoso — carreta-tanque tombada',
        local: 'Rodovia, km 512, sentido capital',
        horario: '14h32',
        recursos: 'ABT, viatura HAZMAT, ambulância',
        informacoes: [
          {
            id: 'i1',
            texto: 'Painel laranja 23 / 1075 visto pelo solicitante.',
            confirmado: false,
            visivel: true,
          },
          {
            id: 'i2',
            texto: 'Cheiro forte de gás no local.',
            confirmado: false,
            visivel: true,
          },
        ],
      },
      objetos: [
        objeto('tanque', 'caminhaoTanque', 'Carreta de GLP', 1.75, 0, 0, {
          fonte: { idProduto: 'glp', enchimento: 0.8, situacao: 'estavel' },
          transformacao: {
            posicao: [1.75, 1.4, 0],
            rotacao: [0, 0, -Math.PI / 2],
            escala: [1, 1, 1],
          },
        }),
        objeto('escola', 'escola', 'Escola municipal', -260, 140),
        objeto('posto', 'predio', 'Posto de combustível', 70, 90),
        objeto('bueiro', 'bueiro', 'Bueiro', 7, 35),
        objeto('biruta', 'biruta', 'Biruta', -14, 20),
        objeto('motorista', 'vitima', 'Motorista', 8, -6),
      ],
    },
  },
  {
    id: 'cloro-urbano',
    titulo: 'Vazamento de cloro em área urbana',
    resumo:
      'Caminhão de cloro com vazamento na válvula, perto de hospital e bairro. Começa com vazamento; o roteiro anoitece e gira o vento.',
    cena: {
      versao: 1,
      nome: 'Vazamento de cloro',
      historiaInicial:
        'Caminhão-tanque de cloro parado com névoa esverdeada saindo da válvula. Bairro residencial e hospital a favor do vento.',
      meteorologia: {
        direcaoVentoGraus: 45,
        ventoMS: 2.5,
        periodo: 'dia',
        ceuEncoberto: false,
        terreno: 'urbano',
      },
      objetos: [
        objeto('tanque', 'caminhaoTanque', 'Caminhão de cloro', 1.75, 0, 0, {
          fonte: {
            idProduto: 'cloro',
            enchimento: 0.85,
            situacao: 'vazamento',
            vazaoKgS: 1,
          },
        }),
        objeto('hospital', 'hospital', 'Hospital regional', -500, 400),
        objeto('bairro', 'predio', 'Bairro', -300, 700),
        objeto('corrego', 'corrego', 'Córrego', -150, 120, 30),
        objeto('biruta', 'biruta', 'Biruta', -14, 20),
      ],
    },
  },
]
